import { useEffect, useMemo, useState } from "react";
import { useVisibilityPause } from "@/lib/useVisibilityPause";

// Respect prefers-reduced-motion globally: when the user has reduced-motion
// turned on we render nothing at all rather than streaming animated flakes
// across every JINGLE surface. This gates SnowField for *every* consumer
// (sign-in, onboarding, marketing, dashboard, legal, 404, join, create)
// without each page having to remember to pass a flag.
function usePrefersReducedMotion(): boolean {
  const [prefers, setPrefers] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefers(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setPrefers(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return prefers;
}

// Mobile devices get noticeably fewer flakes — each flake is an absolutely
// positioned element with a box-shadow halo and a blur filter, so 110 of
// them on a phone meaningfully chews into the GPU compositing budget. The
// breakpoint matches Tailwind's `md` (768px), and the cap is chosen so the
// snow still feels generous but the per-frame cost roughly halves.
function useIsNarrowViewport(): boolean {
  const [narrow, setNarrow] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(max-width: 767px)");
    setNarrow(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setNarrow(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return narrow;
}

export function SnowField({
  flakeCount = 110,
  respectOSReducedMotion = true,
}: {
  flakeCount?: number;
  /**
   * When true (default) the field bails out under the OS
   * `prefers-reduced-motion` setting. Pass `false` to force snow on for users
   * who have explicitly opted into magic in MagicSettings even though their
   * OS reports a reduced-motion preference. Marketing/auth surfaces leave
   * this at the default — they have no per-user opt-in to consult.
   */
  respectOSReducedMotion?: boolean;
}) {
  const osReduced = usePrefersReducedMotion();
  const reduced = osReduced && respectOSReducedMotion;
  const narrow = useIsNarrowViewport();
  // Pause the falling-flake animation while the tab is in the background.
  // The flakes stay mounted so we don't pay a re-render cost when the
  // visitor returns; we just freeze them in place via animation-play-state.
  const docHidden = useVisibilityPause();
  // Cap mobile at ~55% of the requested count. We pick the *minimum* of the
  // requested count and 60 so callers passing already-small values aren't
  // double-discounted.
  const effectiveCount = narrow ? Math.min(flakeCount, 60) : flakeCount;
  const flakes = useMemo(
    () =>
      Array.from({ length: effectiveCount }, (_, i) => {
        const big = Math.random() < 0.18;
        return {
          id: i,
          left: Math.random() * 100,
          delay: Math.random() * -16,
          duration: big ? 14 + Math.random() * 8 : 7 + Math.random() * 8,
          size: big ? 5 + Math.random() * 5 : 1.5 + Math.random() * 3.5,
          opacity: 0.55 + Math.random() * 0.45,
          drift: (Math.random() - 0.5) * 60,
          star: Math.random() < 0.05,
        };
      }),
    // Recompute when the *effective* count changes (e.g., viewport rotates
    // from desktop into mobile-cap territory). Earlier we only depended on
    // `flakeCount`, which meant the memo never refreshed when `narrow`
    // toggled and the mobile cap silently did nothing on rotate.
    [effectiveCount],
  );
  if (reduced) return null;
  return (
    <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
      {flakes.map((f) => (
        <span
          key={f.id}
          className="absolute"
          style={{
            left: `${f.left}%`,
            top: "-6vh",
            width: f.size,
            height: f.size,
            opacity: f.opacity,
            color: "white",
            background: f.star ? "transparent" : "white",
            borderRadius: f.star ? 0 : "9999px",
            boxShadow: f.star
              ? "none"
              : `0 0 ${f.size * 1.5}px rgba(255,255,255,0.7)`,
            filter: "blur(0.4px)",
            animation: `snowdrift ${f.duration}s linear ${f.delay}s infinite`,
            animationPlayState: docHidden ? "paused" : "running",
            // CSS variable consumed by the keyframes below
            ["--drift" as never]: `${f.drift}px`,
          } as React.CSSProperties}
        >
          {f.star ? (
            <svg viewBox="0 0 24 24" width={f.size * 3} height={f.size * 3}>
              <path
                d="M12 2 L13 10 L21 12 L13 14 L12 22 L11 14 L3 12 L11 10 Z"
                fill="white"
                opacity="0.85"
              />
            </svg>
          ) : null}
        </span>
      ))}
      <style>{`
        @keyframes snowdrift {
          0% {
            transform: translateY(-6vh) translateX(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(106vh) translateX(var(--drift, 0px)) rotate(360deg);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
}
